import React from "react";
import PropTypes from "prop-types";

const parseEstimatedArrivalTimeToDate = (value) => {
  if (value === null || value === undefined || value === "") return null;

  // Backend may send:
  // - ISO8601 string: "2026-01-12T14:30:00+01:00"
  // - Unix seconds as string: "1764233360"
  // - Unix millis as string/number
  const asString = String(value).trim();

  // Numeric-only -> treat as unix timestamp
  if (/^\d+$/.test(asString)) {
    const numeric = Number(asString);
    if (!Number.isFinite(numeric)) return null;

    // Heuristic: 10 digits ~= seconds, 13 digits ~= milliseconds
    const ms = asString.length <= 10 ? numeric * 1000 : numeric;
    const date = new Date(ms);
    return isNaN(date.getTime()) ? null : date;
  }

  // Otherwise attempt ISO8601 parse
  const isoDate = new Date(asString);
  return isNaN(isoDate.getTime()) ? null : isoDate;
};

/**
 * Format time string "HH:MM:SS" to "HHhMM"
 */
const formatTimeString = (timeStr) => {
  if (!timeStr) return null;
  const parts = timeStr.split(":");
  if (parts.length < 2) return null;
  return `${parts[0]}h${parts[1]}`;
};

/**
 * Calculate ETA window (±1 hour around ETA) and format for display
 * @param {string} estimatedArrivalTime - timestamp for estimated arrival
 * @returns {{ start: string, end: string, eta: string } | null}
 */
const calculateETAWindow = (estimatedArrivalTime) => {
  if (!estimatedArrivalTime) return null;

  try {
    const etaDate = parseEstimatedArrivalTimeToDate(estimatedArrivalTime);
    if (!etaDate) return null;

    // Create window: ETA ± 1 hour (matching backend's 2-hour window)
    const startDate = new Date(etaDate.getTime() - 60 * 60 * 1000); // -1 hour
    const endDate = new Date(etaDate.getTime() + 60 * 60 * 1000); // +1 hour

    const formatTime = (date) => {
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      return `${hours}h${minutes}`;
    };

    return {
      start: formatTime(startDate),
      end: formatTime(endDate),
      eta: formatTime(etaDate),
    };
  } catch (error) {
    console.warn(
      "Failed to calculate ETA window:",
      estimatedArrivalTime,
      error
    );
    return null;
  }
};

/**
 * TimeslotDisplay - Shows general delivery timeslot and SMS ETA timeslot
 */
const TimeslotDisplay = ({
  fromTime,
  toTime,
  estimatedArrivalTime,
  isSmsSent,
  isDelivered = false,
}) => {
  const generalStart = formatTimeString(fromTime);
  const generalEnd = formatTimeString(toTime);
  const hasGeneralSlot = generalStart && generalEnd;

  const etaWindow = calculateETAWindow(estimatedArrivalTime);
  const hasSmsSlot = etaWindow && isSmsSent;

  // Don't show anything if delivered or no slots available
  if (isDelivered || (!hasGeneralSlot && !hasSmsSlot)) {
    return null;
  }

  return (
    <div className="timeslot-display">
      {/* General delivery timeslot - matches card text style */}
      {hasGeneralSlot && (
        <div className="timeslot-display__general">
          <i
            className="fas fa-clock timeslot-display__icon"
            aria-hidden="true"
          />
          <span className="timeslot-display__time">
            {generalStart} - {generalEnd}
          </span>
        </div>
      )}

      {/* SMS ETA timeslot - blue bubble with SMS indicator */}
      {hasSmsSlot && (
        <div className="timeslot-display__sms">
          <i
            className="fas fa-sms timeslot-display__sms-icon"
            aria-hidden="true"
          />
          <span className="timeslot-display__sms-time">
            {etaWindow.start} - {etaWindow.end}
          </span>
        </div>
      )}
    </div>
  );
};

TimeslotDisplay.propTypes = {
  fromTime: PropTypes.string,
  toTime: PropTypes.string,
  estimatedArrivalTime: PropTypes.string,
  isSmsSent: PropTypes.bool,
  isDelivered: PropTypes.bool,
};

export default TimeslotDisplay;
